import {Hook, HookClass, NickProcessArtfact} from '@mrp-brasil/nick-process-commons';
import {Observable, of} from 'rxjs';
import {ArtfactKind, NickCalculate} from '@mrp-brasil/nick-data-commons';

@HookClass('entity')
export class ArrayHook {

    @Hook('761d2bb0-2e19-48fb-8b88-c4b8d3995fd9')
    public concat(input: NickProcessArtfact): Observable<NickProcessArtfact> {
        const outputArtifact = new NickProcessArtfact();
        outputArtifact.kind = ArtfactKind.ARRAY;

        outputArtifact.object = [];
        input.object.data.map((item: any[]) => {
            outputArtifact.object = outputArtifact.object.concat(item);
        });

        return of(outputArtifact);
    }

    @Hook('d1044107-ef2a-4fd1-8e95-05f037201aa4')
    public map(input: NickProcessArtfact): Observable<NickProcessArtfact> {
        const outputArtifact = new NickProcessArtfact();
        outputArtifact.kind = ArtfactKind.ANY;

        if (!input.object.data || !(input.object.data instanceof Array))
            input.object.data = [];

        outputArtifact.object = input.object.data.map((obj: any) => {
            return input.object.config.valueAField.split('.').reduce((o: any, i: any) => (o ? o[i] : o), obj);
        });

        return of(outputArtifact);
    }

    @Hook('50968343-9d70-47e4-8ed3-25c0f0943117')
    public group(input: NickProcessArtfact): Observable<NickProcessArtfact> {
        const outputArtifact = new NickProcessArtfact();
        outputArtifact.kind = ArtfactKind.ANY;

        // Garante que input.object.data seja um array, caso contrário, inicializa como vazio
        if (!input.object.data || !(input.object.data instanceof Array)) {
            input.object.data = [];
        }

        // Pega o caminho do campo de agrupamento da configuração
        const fieldPath = input.object.config.valueAField;

        // Função auxiliar para obter o valor de um campo aninhado (como no seu exemplo original)
        const getNestedValue = (obj: any, path: string): any => {
            return path.split('.').reduce((o: any, i: string) => (o ? o[i] : undefined), obj);
        };

        // Usaremos um mapa temporário para construir os grupos eficientemente
        const tempGroupMap = new Map<string, any[]>();
        const orderedKeys: string[] = []; // Para manter a ordem das chaves

        input.object.data.forEach((item: any) => {
            const fieldValue = getNestedValue(item, fieldPath);
            const key = String(fieldValue);

            if (!tempGroupMap.has(key)) {
                tempGroupMap.set(key, []); // Cria o array para o novo grupo
                orderedKeys.push(key);     // Adiciona a chave à lista ordenada
            }
            tempGroupMap.get(key)!.push(item); // Adiciona o item ao grupo existente
        });

        // Converte o mapa temporário para o formato de array de arrays desejado
        const groupedResult: Array<[string, any[]]> = [];
        orderedKeys.forEach(key => {
            groupedResult.push([key, tempGroupMap.get(key)!]);
        });

        // Atribui os resultados agrupados ao outputArtifact
        outputArtifact.object = groupedResult;

        return of(outputArtifact);
    }

    @Hook('a7d1e9fe-fcd6-40a1-8425-072f15f8c716')
    public chunkArray(inputArtifact: NickProcessArtfact): Observable<NickProcessArtfact> {
        const outputArtifact = new NickProcessArtfact();
        outputArtifact.kind = ArtfactKind.ANY;

        const inputData = inputArtifact.object?.data;
        const config = inputArtifact.object?.config;

        // Garante que inputData é um array, caso contrário, inicializa como vazio
        if (!Array.isArray(inputData)) {
            console.warn('Input \'data\' is not an array or is missing. Returning empty array.');
            outputArtifact.object = [];
            return of(outputArtifact);
        }

        // Obtém o tamanho do chunk do objeto de configuração
        // Use um valor padrão razoável se não for fornecido ou for inválido
        const chunkSize = typeof config?.batchSize === 'number' && config.batchSize > 0
            ? config.batchSize
            : 100; // Tamanho de chunk padrão, ajuste conforme necessário

        const chunkedArray: any[][] = [];
        for (let i = 0; i < inputData.length; i += chunkSize) {
            chunkedArray.push(inputData.slice(i, i + chunkSize));
        }

        outputArtifact.object = chunkedArray;

        return of(outputArtifact);
    }

    @Hook('a3a80a71-3559-42b4-94dd-e4da0bc09ec6')
    public removeDuplicates(input: NickProcessArtfact): Observable<NickProcessArtfact> {
        if (input.object.config?.returnObjects == true) {
            const uniqueValues = new Set();
            const uniqueObjects: any[] = [];

            input.object.data.forEach((obj: any) => {
                const fieldValue = input.object.config?.field ?
                    input.object.config.field.split('.').reduce((o: any, i: any) => (o ? o[i] : o), obj)
                    : obj;

                if (!uniqueValues.has(fieldValue)) {
                    uniqueValues.add(fieldValue);
                    uniqueObjects.push(obj);
                }
            });

            const outputArtifact = new NickProcessArtfact();
            outputArtifact.kind = ArtfactKind.ANY;
            outputArtifact.object = uniqueObjects;

            return of(outputArtifact);
        } else {
            let value = input.object.data;
            if (input.object.config?.field && (input.object.data instanceof Array))
                value = input.object.data
                    .map((_value: any) => input.object.config?.field.split('.')
                        .reduce((o: any, i: any) => (o ? o[i] : o), _value));

            const outputArtifact = new NickProcessArtfact();
            outputArtifact.kind = ArtfactKind.ANY;
            outputArtifact.object = [...new Set(value)];
            return of(outputArtifact);
        }
    }

    @Hook('94e55c04-578d-404c-9e66-a069713550be')
    public filterIncludes(input: NickProcessArtfact): Observable<NickProcessArtfact> {
        const outputArtifact = new NickProcessArtfact();
        outputArtifact.kind = ArtfactKind.ANY;

        const valueAField = input.object.config.valueAField;
        const result = [];
        for (let i = 0; i < input.object.data.length; i++) {
            for (let j = 0; j < input.object.config.filterArray.length; j++) {
                if (valueAField) {
                    if (input.object.config.filterArray[j][valueAField] == input.object.data[i][valueAField]) {
                        result.push(input.object.data[i]);
                        break;
                    }
                } else {
                    if (input.object.config.filterArray[j] == input.object.data[i]) {
                        result.push(input.object.data[i]);
                        break;
                    }
                }
            }
        }
        outputArtifact.object = result;

        return of(outputArtifact);
    }

    @Hook('eab8089b-818c-43ab-b895-72184f999b90')
    public filter(input: NickProcessArtfact): Observable<NickProcessArtfact> {

        const inputArtifact = new NickProcessArtfact();
        let valueA = input.object.config.valueA;
        let valueB = input.object.config.valueB;
        inputArtifact.kind = ArtfactKind.ANY;

        inputArtifact.object = input.object.data.filter((item: any) => {
            if (input.object.config.valueAField)
                valueA = input.object.config.valueAField.split('.').reduce((o: any, i: any) => (o ? o[i] : o), item);

            if (input.object.config.valueBField)
                valueB = input.object.config.valueBField.split('.').reduce((o: any, i: any) => (o ? o[i] : o), item);
            return NickCalculate.applyCondition(valueA, valueB, input.object.config.conditionKind);
        });

        return of(inputArtifact);
    }

    @Hook('2c7c4b23-0f5a-44dd-ac06-c4a87c67aed3')
    public createFromTemplate(inputArtifact: NickProcessArtfact): Observable<NickProcessArtfact> {
        const outputArtifact = new NickProcessArtfact();
        outputArtifact.kind = ArtfactKind.ANY;
        outputArtifact.object = {};

        const size = inputArtifact.object.data.size;
        const template = inputArtifact.object.data.template;
        const templateReplace = inputArtifact.object.data.templateReplace;
        const data = [];
        for (let i = 0; i < size; i++) {
            const item = Object.assign({}, template);
            // when flow foreach hook support index, this block of code can be called separately
            if (templateReplace && templateReplace.length > 0) {
                for (let templateIndex = 0; templateIndex < templateReplace.length; templateIndex++) {
                    const replaceItem = templateReplace[templateIndex];
                    let replaceItemValue = replaceItem.config.template;
                    if (typeof replaceItemValue == 'string') {
                        Object.keys(replaceItem.data).forEach((key: string) => {
                            const keyWithBrackets = '{{' + key + '}}';

                            if (key == '__currentIndex') {
                                replaceItemValue = replaceItemValue.replace(keyWithBrackets, i + 1);
                            } else if (key == '__totalIndex') {
                                replaceItemValue = replaceItemValue.replace(keyWithBrackets, size);
                            } else {
                                replaceItemValue = replaceItemValue.replace(keyWithBrackets, replaceItem.data[key]);
                            }
                        });
                        item[replaceItem.key] = replaceItemValue;
                    }
                }
            }

            data.push(item);
        }
        outputArtifact.object.data = data;
        return of(outputArtifact);
    }

    @Hook('9e2b565a-7b21-42c1-84bc-c705c42a60ee')
    public push(inputArtifact: NickProcessArtfact): Observable<NickProcessArtfact> {
        if (!inputArtifact.object.data.array)
            inputArtifact.object.data.array = [];

        inputArtifact.object.data.array.push(inputArtifact.object.data.item);
        return of(inputArtifact);
    }

    @Hook('aa2efbb4-2a7c-4976-aaee-b928b2142463')
    public splice(inputArtifact: NickProcessArtfact): Observable<NickProcessArtfact> {
        inputArtifact.object.data = inputArtifact.object.data.splice(inputArtifact.object.config.start, inputArtifact.object.config.end);
        return of(inputArtifact);
    }

    @Hook('6e557fcc-5e6d-4578-96f0-632a56eb1cab')
    public sortArrayObjects(inputArtifact: NickProcessArtfact): Observable<NickProcessArtfact> {
        const input = inputArtifact.object.data;
        const config = inputArtifact.object.config.fields;
        const type = inputArtifact.object.config.type ? inputArtifact.object.config.type : 'any';

        const _orderByComparator = (a: any, b: any, type?: string): number => {


            if (a === null || typeof a === 'undefined') a = 0;
            if (b === null || typeof b === 'undefined') b = 0;

            if (type == 'date') {
                a = a != 0 ? new Date(a).getTime() : new Date(8640000000000000).getTime();
                b = b != 0 ? new Date(b).getTime() : new Date(8640000000000000).getTime();
            }

            if ((isNaN(parseFloat(a)) || !isFinite(a)) || (isNaN(parseFloat(b)) || !isFinite(b))) {
                // Isn't a number so lowercase the string to properly compare
                if (a.toLowerCase() < b.toLowerCase()) return -1;
                if (a.toLowerCase() > b.toLowerCase()) return 1;
            } else {
                // Parse strings as numbers to compare properly
                if (parseFloat(a) < parseFloat(b)) return -1;
                if (parseFloat(a) > parseFloat(b)) return 1;
            }

            return 0; // equal each other
        };

        if (!input) return input;
        const value = [...input];
        if (!Array.isArray(value)) return value;

        if (!Array.isArray(config) || (Array.isArray(config) && config.length === 1)) {
            const propertyToCheck: string = !Array.isArray(config) ? config : config[0];
            const desc = propertyToCheck.substr(0, 1) === '-';
            const field = propertyToCheck.substr(1);
            if (propertyToCheck || propertyToCheck === '-' || propertyToCheck === '+') {

                value.sort((a: any, b: any) => {
                    const reduceA = field.split('.').reduce((o: any, i: any) => (o ? o[i] : o), a);
                    const reduceB = field.split('.').reduce((o: any, i: any) => (o ? o[i] : o), b);
                    // input.object.config.valueAField.split('.').reduce((o: any, i: any) => (o ? o[i] : o), obj);
                    return !desc ? inputArtifact.object.data = _orderByComparator(reduceA, reduceB, type) : inputArtifact.object.data = _orderByComparator(reduceA, reduceB, type);
                }).reverse();

                inputArtifact.object.data = !desc ? value.sort() : value.sort().reverse();
                return of(inputArtifact);
            } else {
                const property: string = propertyToCheck.substr(0, 1) === '+' || propertyToCheck.substr(0, 1) === '-'
                    ? propertyToCheck.substr(1) : propertyToCheck;
                value.sort((a: any, b: any) => {
                    const reduceA = field.split('.').reduce((o: any, i: any) => (o ? o[i] : o), a);
                    const reduceB = field.split('.').reduce((o: any, i: any) => (o ? o[i] : o), b);
                    // input.object.config.valueAField.split('.').reduce((o: any, i: any) => (o ? o[i] : o), obj);
                    return !desc ? inputArtifact.object.data = _orderByComparator(reduceA, reduceB, type) : inputArtifact.object.data = _orderByComparator(reduceA, reduceB, type);
                }).reverse();
                inputArtifact.object.data = !desc ? value.sort() : value.sort().reverse();
                return of(inputArtifact);
            }
        } else {
            inputArtifact.object.data = value.sort((a: any, b: any) => {
                // tslint:disable-next-line:prefer-for-of
                for (let i = 0; i < config.length; i++) {
                    const desc = config[i].substr(0, 1) === '-';
                    const property = config[i].substr(0, 1) === '+' || config[i].substr(0, 1) === '-'
                        ? config[i].substr(1)
                        : config[i];

                    const reduceA = property.split('.').reduce((o: any, i: any) => (o ? o[i] : o), a);
                    const reduceB = property.split('.').reduce((o: any, i: any) => (o ? o[i] : o), b);

                    const comparison = !desc
                        ? _orderByComparator(reduceA, reduceB, type)
                        : -_orderByComparator(reduceA, reduceB, type);

                    // Don't return 0 yet in case of needing to sort by next property
                    if (comparison !== 0) return comparison;
                }
                return 0;
            });
            return of(inputArtifact);
        }

    }

    @Hook('f767eac4-e5bc-478e-a913-f83047990df3')
    public arrayApply(inputArtifact: NickProcessArtfact): Observable<NickProcessArtfact> {
        const data = inputArtifact.object.data;

        const outputArtifact = new NickProcessArtfact();
        outputArtifact.kind = ArtfactKind.ANY;

        if (data instanceof Array) {
            // @ts-ignore
            outputArtifact.object = Array.prototype[inputArtifact.object.config.name].apply(...data);
        } else {
            // @ts-ignore
            outputArtifact.object = Array.prototype[inputArtifact.object.config.name].apply(data);
        }

        return of(outputArtifact);

    }

    @Hook('9c768082-d461-4ec4-8278-7884c865ddcd')
    public getAtPosition(inputArtifact: NickProcessArtfact): Observable<NickProcessArtfact> {
        const outputArtifact = new NickProcessArtfact();
        outputArtifact.kind = ArtfactKind.ANY;
        outputArtifact.object = inputArtifact.object.data[inputArtifact.object.index];
        return of(outputArtifact);
    }

    @Hook('209fe875-5666-4be8-a478-6b0776f84d0c')
    public spreadArray(inputArtifact: NickProcessArtfact): Observable<NickProcessArtfact> {
        const outputArtifact = new NickProcessArtfact();
        outputArtifact.kind = ArtfactKind.ANY;
        outputArtifact.object = {};
        const flattenDeep = (arr1: any) => {
            return arr1.reduce((acc: any, val: any) => Array.isArray(val) ? acc.concat(flattenDeep(val)) : acc.concat(val), []);
        };
        outputArtifact.object.data = flattenDeep(inputArtifact.object.data);
        return of(outputArtifact);
    }

    @Hook('6bdd56c8-5e3e-457f-874f-7d722c8dfde0')
    public hashMap(inputArtifact: NickProcessArtfact): Observable<NickProcessArtfact> {
        const outputArtifact = new NickProcessArtfact();
        outputArtifact.kind = ArtfactKind.ANY;
        outputArtifact.object = new Map<any, any>();

        const field = inputArtifact.object.config?.field.split('.');
        inputArtifact.object.data?.forEach((value: any) => {
            const keyField = field.reduce((o: any, i: any) => (o ? o[i] : o), value);
            outputArtifact.object.set(keyField, value);
        });

        return of(outputArtifact);
    }

    @Hook('bf6ad501-6f2e-4280-81ac-a3146f01e2dc')
    public hashMapApply(input: NickProcessArtfact): Observable<NickProcessArtfact> {
        const data = input.object.data;

        const outputArtifact = new NickProcessArtfact();
        outputArtifact.kind = ArtfactKind.ANY;
        // @ts-ignore
        if (data instanceof Array) {
            // @ts-ignore
            outputArtifact.object = input.object.map[input.object.config.name](...data);
        } else {
            // @ts-ignore
            outputArtifact.object = input.object.map[input.object.config.name](data);
        }
        return of(outputArtifact);
    }

    @Hook('a016ad34-e7b9-4f51-93d8-de6dfb0d1760')
    public arrayFromMap(input: NickProcessArtfact): Observable<NickProcessArtfact> {
        const outputArtifact = new NickProcessArtfact();
        outputArtifact.kind = ArtfactKind.ANY;
        outputArtifact.object = Array.from(input.object.map.values());
        return of(outputArtifact);
    }

    @Hook('38937bbc-3578-4ca2-bb00-c384f9a23e77')
    public concatEqualObjects(input: NickProcessArtfact): Observable<NickProcessArtfact> {
        const outputArtifact = new NickProcessArtfact();
        outputArtifact.kind = ArtfactKind.ANY;
        const array = input.object.data;
        const finalObject = {...array[0]}; // copy the first object

        // Start from the second object in the array
        for (let i = 1; i < array.length; i++) {
            const obj = array[i];

            // Iterate over the keys of the object
            for (const key in obj) {
                // Check if a property with the same key already exists in finalObject
                if (finalObject.hasOwnProperty(key)) {
                    // If it exists, concatenate the value
                    for (let j = 0 ; j < obj[key].length; j++) {
                        finalObject[key].push(obj[key][j]);
                    }

                } else {
                    // If it doesn't exist, create a new property and add it to finalObject
                    finalObject[key] = obj[key];
                }
            }
        }
        outputArtifact.object = finalObject;
        return of(outputArtifact);

    }

    @Hook('38f6ed98-9757-406a-83aa-b75d12cebd38')
    public updateAttrObject(inputArtifact: NickProcessArtfact): Observable<NickProcessArtfact> {
        // Cria uma cópia de `data` para preservar a integridade do objeto original
        const data = Object.assign({}, inputArtifact.object.data);
        const editAttributes = inputArtifact.object.edit; // editar Atributos
        const newAttributes = inputArtifact.object.new;  // adicionar Atributos

        const outputArtifact = new NickProcessArtfact();
        outputArtifact.kind = ArtfactKind.ANY;

        // Verifica se `data` é um objeto válido
        if (data && typeof data === 'object' && !Array.isArray(data)) {
            // Atualiza os valores existentes com base em `edit`
            if (editAttributes && typeof editAttributes === 'object') {
                Object.keys(editAttributes).forEach(chave => {
                    if (data.hasOwnProperty(chave)) {
                        data[chave] = editAttributes[chave];
                    }
                });
            }

            // Adiciona novos atributos com base em `new`
            if (newAttributes && typeof newAttributes === 'object') {
                Object.keys(newAttributes).forEach(chave => {
                    if (!data.hasOwnProperty(chave)) {
                        data[chave] = newAttributes[chave];
                    }
                });
            }

            // Atribui o objeto atualizado ao `outputArtifact`
            outputArtifact.object = data;
        } else {
            // Caso `data` não seja válido, retorna o input sem alterações
            outputArtifact.object = inputArtifact.object.data;
        }

        return of(outputArtifact);
    }

    @Hook('66113670-5c9f-491b-a292-347a9e762de8')
    public normalizeKeys(inputArtifact: NickProcessArtfact): Observable<NickProcessArtfact> {
        const outputArtifact = new NickProcessArtfact();
        outputArtifact.kind = ArtfactKind.ANY;

        const data = inputArtifact.object.data as Record<string, any>[];
        const keys = inputArtifact.object.keys as string[];

        outputArtifact.object = data.map((obj: any) =>
            Object.fromEntries(
                keys.map((key: any) => [key, obj[key] ?? null])
            ))

        return of(outputArtifact);

    }

}

